/**
 * Scan decrypted document bodies to find which docs reference a given
 * attachment. Drives the "used in" labels in AttachmentsView.
 *
 * Reference forms in doc markdown:
 *   - Inline images    — `<inline-image … attachmentId="ID" …></inline-image>`
 *   - Attachment chips — `<attachment-chip … attachmentId="ID" …></attachment-chip>`
 */

import { listDecryptedDocuments } from '../sync';
import { getLiveAttachmentIds, getLiveDerivedMarkdown } from './yjsCollab';

export interface DocRef {
  docId: string;
  title: string;
  /** Parent page title, set only when the ref lives on a sub-document (tab). */
  parentTitle?: string;
  /** Parent page id, or null when this doc IS a top-level page. */
  parentId: string | null;
}

const INLINE_IMAGE_RE = /<inline-image\s+[^>]*attachmentId\s*=\s*"([^"]+)"/gi;
const ATTACHMENT_CHIP_RE = /<attachment-chip\s+[^>]*attachmentId\s*=\s*"([^"]+)"/gi;

/** Returns the unique set of attachment ids referenced anywhere in `md`. */
export function extractAttachmentIdsFromMarkdown(md: string): Set<string> {
  const ids = new Set<string>();
  if (!md) return ids;
  for (const re of [INLINE_IMAGE_RE, ATTACHMENT_CHIP_RE]) {
    re.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(md)) !== null) {
      if (m[1]) ids.add(m[1]);
    }
  }
  return ids;
}

/**
 * Builds `Map<attachmentId, DocRef[]>` covering every doc in the given
 * customer's scope. Uses the IndexedDB cache (via `listDecryptedDocuments`),
 * so no extra network calls.
 */
export async function buildReferenceMap(
  customerId: string,
  vaultKey: CryptoKey,
): Promise<Map<string, DocRef[]>> {
  const docs = await listDecryptedDocuments(customerId, vaultKey);
  const titleById = new Map<string, string>();
  for (const d of docs) titleById.set(d.id, d.title || '(untitled)');

  const map = new Map<string, DocRef[]>();
  for (const d of docs) {
    // Prefer the live Y.Doc's derivedMarkdown when the doc is currently
    // open — IndexedDB only catches up after a 3s snapshot debounce, so a
    // freshly-inserted reference would otherwise appear "Unreferenced".
    const md = getLiveDerivedMarkdown(d.id) ?? d.markdown;
    const ids = extractAttachmentIdsFromMarkdown(md);
    // Belt-and-braces: when the doc is loaded in the editor, also walk the
    // Y.Doc body directly. This catches atom nodes (inline-image,
    // attachment-chip) even if they were historically stripped from the
    // stored markdown by Turndown's blank-element suppression.
    const liveIds = getLiveAttachmentIds(d.id);
    if (liveIds) for (const id of liveIds) ids.add(id);
    if (ids.size === 0) continue;
    const ref: DocRef = {
      docId: d.id,
      title: d.title || '(untitled)',
      parentId: d.parentId,
      parentTitle:
        d.parentId != null ? titleById.get(d.parentId) ?? '(untitled)' : undefined,
    };
    for (const id of ids) {
      const list = map.get(id);
      if (list) list.push(ref);
      else map.set(id, [ref]);
    }
  }
  return map;
}
